import Box from '@mui/material/Box';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useParams } from 'react-router';
import { mindtimeWazuhVulnerabilitiesApi } from 'src/api/mindtime/wazuhVulnerabilitiesApi';
import { getPageTitle } from 'src/config/projectConfig';
import { getSecurityNavItemPath } from 'src/mindtime/config/securityNav';
import { EdrEntityDetailPage } from '../shared/edrPrimitives';
import type { WazuhVulnerabilityItem } from 'src/types/mindtime/wazuhVulnerabilities';
import logger from 'src/utils/logger';
import { buildVulnerabilityDetailProps } from './vulnerabilityDetailModel';

type VulnerabilityDetailLocationState = {
  record?: WazuhVulnerabilityItem;
};

const VulnerabilityDetail: FC = () => {
  const { recordId = '' } = useParams<{ recordId: string }>();
  const location = useLocation();
  const vulnerabilityId = decodeURIComponent(recordId).trim();
  const stateRecord = (location.state as VulnerabilityDetailLocationState | null)?.record;

  const [record, setRecord] = useState<WazuhVulnerabilityItem | null>(stateRecord ?? null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!vulnerabilityId) {
      setRecord(null);
      setLoading(false);
      setNotFound(true);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    mindtimeWazuhVulnerabilitiesApi
      .getVulnerability(vulnerabilityId)
      .then((data) => {
        if (cancelled) return;
        setRecord(data);
        setNotFound(false);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'VulnerabilityDetail');
          setRecord(null);
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [vulnerabilityId]);

  const detailProps = record ? buildVulnerabilityDetailProps(record) : null;
  const listHref = getSecurityNavItemPath('vulnerability-detection') ?? '';

  return (
    <>
      <Helmet>
        <title>{getPageTitle(`Security — ${record?.cveId ?? 'Vulnerability'}`)}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{
          gap: 16,
          minHeight: '100%',
          boxSizing: 'border-box',
          background: '#F8FAFC',
        }}
      >
        <EdrEntityDetailPage
          backLink={detailProps?.backLink ?? { label: 'Back to Vulnerabilities', to: listHref }}
          title={detailProps?.title ?? 'Vulnerability'}
          badges={detailProps?.badges}
          metaLine={detailProps?.metaLine}
          main={detailProps?.main ?? null}
          sidebar={detailProps?.sidebar}
          loading={loading}
          notFoundMessage={notFound ? 'Vulnerability record not found.' : undefined}
        />
      </Box>
    </>
  );
};

export default VulnerabilityDetail;
